'use client';

import { useEffect, useRef, type ReactNode } from 'react';

import { setBearerToken } from '@/src/api/auth-client';
import { replaceWhenAuthNavigationReady } from './auth-navigation-hold';
import { subscribeAuthTerminalEvents } from './auth-lifecycle';
import { probeStoredSessionAuthority } from './session-authority';
import { clearAuthStorage, getAuthGeneration } from './token-storage';

export function AuthLifecycleCoordinator({ children }: { children: ReactNode }) {
  const authorityProbeRef = useRef<Promise<void> | null>(null);
  const pendingProbeRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    const convergeInvalidSession = (generation: number) => {
      if (!mounted) return;
      // A stale probe must not clear authority established while it was in flight.
      // Local convergence must not emit another BroadcastChannel event.
      if (clearAuthStorage(generation) === null) return;
      setBearerToken(null);
      replaceWhenAuthNavigationReady('/login?reason=session-expired');
    };

    const requestAuthorityProbe = () => {
      if (authorityProbeRef.current) {
        pendingProbeRef.current = true;
        return;
      }

      pendingProbeRef.current = false;
      const generation = getAuthGeneration();
      const probe = Promise.resolve()
        .then(() => probeStoredSessionAuthority())
        .then((result) => {
          if (result.status === 'invalid-session') convergeInvalidSession(generation);
        })
        .catch(() => {
          // A failed authority probe is transient and must preserve local auth.
        })
        .finally(() => {
          authorityProbeRef.current = null;
          if (mounted && pendingProbeRef.current) requestAuthorityProbe();
        });

      authorityProbeRef.current = probe;
    };

    const unsubscribe = subscribeAuthTerminalEvents(() => {
      // Cross-tab events are untrusted hints. Verify the exact bearer session
      // through a read-only protected endpoint before clearing local authority.
      requestAuthorityProbe();
    });
    const revalidateForegroundAuthority = () => requestAuthorityProbe();
    const revalidateVisibleAuthority = () => {
      if (document.visibilityState === 'visible') requestAuthorityProbe();
    };

    window.addEventListener('pageshow', revalidateForegroundAuthority);
    document.addEventListener('visibilitychange', revalidateVisibleAuthority);

    return () => {
      mounted = false;
      pendingProbeRef.current = false;
      unsubscribe();
      window.removeEventListener('pageshow', revalidateForegroundAuthority);
      document.removeEventListener('visibilitychange', revalidateVisibleAuthority);
    };
  }, []);

  return <>{children}</>;
}
